import axios, { InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';

import { generateCartId, getCartId } from '../utils/cart';
import { getSubdomain } from '../utils/subdomain';
import redirectToAuth from '../utils/redirect';

const retriesConfig = [2000, 2000, 2000, 2000, 3000, 3000, 4000, 4000, 5000, 5000, 8000];

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 60000,
  headers: {
    'X-Tenant-Id': getSubdomain(),
  },
});

axiosRetry(api, {
  retries: retriesConfig.length,
  retryDelay: (retryCount) => retriesConfig[retryCount],
});

/**
 * Exported so it can be unit tested directly. Reaching into
 * `api.interceptors.request.handlers` to exercise it would test axios internals instead.
 */
export const applyRequestHeaders = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  if (!getCartId()) {
    generateCartId();
  }
  const cartId = getCartId();

  config.headers['X-Cart-Id'] = cartId;

  // Calls that must reach the API as anonymous even while a customer is signed in.
  //
  // `hooks/auth.tsx` stores the customer token in `api.defaults.headers.common`, so by the time an
  // interceptor runs it is already merged into `config.headers` and has to be removed explicitly.
  // Endpoints that only accept an operator credential reject a customer token outright, which would
  // otherwise make the feature unusable for exactly the customers who bothered to sign in.
  if (config.skipAuthHeader) {
    config.headers.delete('Authorization');
  }

  return config;
};

api.interceptors.request.use(applyRequestHeaders);

/**
 * The page-level redirects for a failed response: 401 to the auth server, 404 to `/error`, 5xx to
 * `/server-error`. Returns whether any of them fired; any other failure (no response at all, or a
 * status such as 400, 403, 409, 422 or 429) navigates nowhere and returns `false`.
 *
 * It is the single place these redirects live. The response interceptor below uses it, and so does
 * `loadTable`, which opts out of the interceptor and applies the same redirects itself only after
 * checking that its table session is still the current one: a late answer from a session that was
 * already replaced must not navigate the customer anywhere.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const applyErrorRedirect = (error: any): boolean => {
  let redirected = false;

  if (error?.response && error?.response?.status === 401) {
    redirectToAuth({ redirect: 'menu' });
    redirected = true;
  }

  if (error?.response && error?.response?.status === 404) {
    window.location.pathname = '/error';
    redirected = true;
  }

  if (error?.response && error?.response?.status >= 500) {
    window.location.pathname = '/server-error';
    redirected = true;
  }

  return redirected;
};

/**
 * Exported for the same reason as `applyRequestHeaders`: this navigates the whole app, so the
 * opt-out has to be proven against the real handler rather than by asserting that a flag was passed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleResponseError = (error: any): Promise<never> => {
  // Per-request opt-out for calls that must never navigate the customer away from where they are.
  // Handling the error at the call site is not enough: these redirects fire here, in the
  // interceptor, before the caller's catch ever runs.
  if (error?.config?.skipErrorRedirect) {
    return Promise.reject(error);
  }

  applyErrorRedirect(error);

  return Promise.reject(error);
};

api.interceptors.response.use((response) => response, handleResponseError);

export default api;
